/**
 * Phone-number validation — the client mirror of `server/src/utils.js`
 * `validatePhone`. Keep the two in sync: this one drives inline form messages;
 * the server copy is the authoritative check.
 *
 * The platform serves Kerala only, so a number is exactly a 10-digit Indian
 * mobile — no country code is asked for and none is stored. A pasted +91 / 91 /
 * leading 0 is tolerated and stripped, because that is how numbers arrive from
 * a phone's contact list.
 */
/** An Indian mobile number is exactly 10 digits. */
export const PHONE_DIGITS = 10;

/**
 * Reduces any accepted spelling of a number to its 10 significant digits.
 * Shared with the input so typing, pasting and validating all agree.
 */
export function phoneDigits(raw) {
  let digits = String(raw ?? '').replace(/\D/g, '');
  // Peel off a dialling prefix however it was pasted — +91, 0091, or a leading
  // STD 0. Each branch shortens the string, so this always terminates.
  while (digits.length > PHONE_DIGITS) {
    if (digits.startsWith('0')) digits = digits.replace(/^0+/, '');
    else if (digits.startsWith('91')) digits = digits.slice(2);
    else break;
  }
  return digits;
}

export function validatePhone(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return { ok: false, error: 'Phone number is required' };
  if (/[^\d\s()+\-.]/.test(trimmed)) {
    return { ok: false, error: 'Phone number can only contain digits and + - ( ) spaces' };
  }

  const digits = phoneDigits(trimmed);

  if (digits.length < PHONE_DIGITS) {
    return { ok: false, error: `Enter a ${PHONE_DIGITS}-digit mobile number` };
  }
  if (digits.length > PHONE_DIGITS) {
    return { ok: false, error: `Phone number is too long — ${PHONE_DIGITS} digits only` };
  }
  if (!/^[6-9]/.test(digits)) {
    return { ok: false, error: 'An Indian mobile number starts with 6, 7, 8 or 9' };
  }
  return { ok: true, value: digits };
}

/** Convenience boolean used for enabling/disabling submit buttons. */
export const isValidPhone = (raw) => validatePhone(raw).ok;
